import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api/client';
import { Empty, ErrorNotice, Icon, Loading } from '../../components/ui';
import { useAsync } from '../../lib/hooks';
import { currentPeriod, monthShort, MODULE_LABEL, relativeDays, shiftPeriod } from '../../lib/format';
import { matchesSearch } from '../../lib/search';

export function ClientsPage() {
  const { data, error, loading, reload } = useAsync(() => api.clients(), []);
  const [query, setQuery] = useState('');
  const lastMonth = shiftPeriod(currentPeriod(), -1);

  const list = (data ?? []).filter((c) => matchesSearch(query, c.name, c.contactName));
  const active = (data ?? []).filter((c) => c.isActive);
  const behind = active.filter(
    (c) => c.modules.some((m) => m === 'social' || m === 'site' || m === 'gmb') && (c.lastPeriod ?? '') < lastMonth,
  ).length;
  const pending = active.reduce((s, c) => s + c.pendingDeliverables, 0);

  return (
    <>
      <div className="page-head">
        <div className="stack" style={{ gap: 6 }}>
          <p className="eyebrow">&lt;_Painel_Admin/&gt;</p>
          <h1 className="t-display">Clientes</h1>
          {data && (
            <p className="t-sub">
              {active.length} ativo{active.length === 1 ? '' : 's'} · {behind} sem lançamento de {monthShort(lastMonth)} · {pending}{' '}
              entregáve{pending === 1 ? 'l' : 'is'} em andamento
            </p>
          )}
        </div>
        <Link to="/admin/clientes/novo" className="btn btn--action">
          <Icon name="plus" size={16} />
          Novo cliente
        </Link>
      </div>

      {loading && !data ? (
        <Loading />
      ) : error ? (
        <ErrorNotice message={error} onRetry={reload} />
      ) : (
        data &&
        (data.length === 0 ? (
          <Empty
            title="Nenhum cliente cadastrado"
            action={
              <Link to="/admin/clientes/novo" className="btn btn--action btn--sm">
                Cadastrar o primeiro cliente
              </Link>
            }
          >
            Cadastre um cliente, escolha os serviços contratados e crie o acesso dele ao painel.
          </Empty>
        ) : (
          <div className="stack">
            <div className="field" style={{ maxWidth: 360 }}>
              <label htmlFor="busca" className="sr-only">
                Buscar cliente
              </label>
              <input
                id="busca"
                className="input"
                type="search"
                placeholder="Buscar por empresa ou contratante"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="client-list">
              {list.map((c) => {
                const hasMetrics = c.modules.some((m) => m === 'social' || m === 'site' || m === 'gmb');
                const late = c.isActive && hasMetrics && (c.lastPeriod ?? '') < lastMonth;
                return (
                  <Link key={c.id} to={`/admin/clientes/${c.id}`} className={`client-row ${c.isActive ? '' : 'client-row--off'}`}>
                    <div className="stack" style={{ gap: 0, minWidth: 0 }}>
                      <span className="client-row__name">{c.name}</span>
                      <span className="t-caption">
                        {c.isActive ? (c.contactName ?? 'Sem contratante') : ['Desativado', c.contactName].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <div className="client-row__mods">
                      {c.modules.length === 0 ? (
                        <span className="t-caption">Nenhum módulo ativo</span>
                      ) : (
                        c.modules.map((m) => (
                          <span key={m} className="chip">
                            {MODULE_LABEL[m]}
                          </span>
                        ))
                      )}
                    </div>
                    <div className="client-row__stat">
                      <span className="t-caption">Último lançamento</span>
                      <b style={late ? { color: 'var(--warn)' } : undefined}>
                        {hasMetrics ? (c.lastPeriod ? monthShort(c.lastPeriod) : 'nenhum') : '—'}
                      </b>
                    </div>
                    <div className="client-row__stat">
                      <span className="t-caption">Último acesso</span>
                      <b>{relativeDays(c.lastLoginAt)}</b>
                    </div>
                    <span className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                      {c.pendingDeliverables > 0 && <span className="tag tag--hl">{c.pendingDeliverables} em andamento</span>}
                      <Icon name="chevronRight" />
                    </span>
                  </Link>
                );
              })}
              {list.length === 0 && <p className="muted t-small">Nenhum cliente com “{query}”.</p>}
            </div>
          </div>
        ))
      )}
    </>
  );
}
